import { ImageProps } from 'next/image'
import { FC, useEffect, useMemo, useRef, useState } from 'react'

import Highlight from '../Highlight'
import { LinkProps } from '../A'

import * as SC from './styled'

export type ContentCardProps = {
  id: string
  className?: string
  imageProps?: ImageProps & { fallbacksrc?: string }
  backgroundColor?: string
  firstHighlightText?: string
  secondHighlightText?: string
  duration?: string
  buttons?: JSX.Element
  forceLandscape?: boolean
  singleButtonActionProps?: LinkProps
  tag?: string
  date?: string
  location?: string
  locationImage?: ImageProps
  variant?: SC.ContentCardVariant
}

const ContentCard: FC<ContentCardProps> = (props) => {
  const {
    className,
    imageProps,
    backgroundColor,
    firstHighlightText,
    secondHighlightText,
    duration = 0,
    buttons,
    forceLandscape = false,
    singleButtonActionProps,
    tag,
    date,
    location,
    locationImage,
    variant = SC.ContentCardVariant.Default,
  } = props
  const [imgSrc, imgSrcSet] = useState(imageProps?.src)

  const buttonContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const seeRecipeButton = buttonContainerRef.current
      ?.childNodes[0] as HTMLElement
    const changeRecipeButton = buttonContainerRef.current?.childNodes[1]

    changeRecipeButton?.addEventListener('mouseover', () => {
      seeRecipeButton?.classList.add('changeButtonHovered')
    })

    changeRecipeButton?.addEventListener('mouseout', () => {
      seeRecipeButton?.classList.remove('changeButtonHovered')
    })
  }, [])

  const CardContent = useMemo(() => {
    return (
      <>
        <SC.Background>
          <SC.TextureSection
            className="texture-section"
            backgroundColor={backgroundColor}
          >
            <SC.BgTexture />
          </SC.TextureSection>
          <SC.ImageSection>
            {locationImage && (
              <SC.LocationWrapper>
                <SC.LocationImage {...locationImage} />
              </SC.LocationWrapper>
            )}
            {variant === 'default' && imageProps && imgSrc && (
              <SC.CImage
                {...imageProps}
                $variant={variant}
                src={imgSrc}
                onError={() => {
                  imgSrcSet(imageProps?.fallbacksrc)
                }}
              />
            )}
          </SC.ImageSection>
        </SC.Background>
        <SC.Foreground has3rows={!!duration}>
          {firstHighlightText && (
            <SC.FirstHighlight>
              <Highlight text={firstHighlightText} transparent></Highlight>
            </SC.FirstHighlight>
          )}
          {secondHighlightText && (
            <SC.SecondHighlight>
              <Highlight text={secondHighlightText}></Highlight>
            </SC.SecondHighlight>
          )}
          {(!!duration || !!tag || !!location) && (
            <SC.ThirdHighlight>
              {!!duration && (
                <SC.Duration>
                  <SC.ClockIcon icon="clock" />
                  {duration}
                </SC.Duration>
              )}
              {!!tag && (
                <SC.Tag>
                  <SC.SportIcon icon="intensity" />
                  <p>{tag}</p>
                </SC.Tag>
              )}
              {!!date && (
                <SC.Date>
                  <SC.SportIcon icon="clockTransparent" />
                  <p>{date}</p>
                </SC.Date>
              )}
              {!!location && (
                <SC.Location>
                  <SC.SearchIcon icon="search" />
                  <p>{location}</p>
                </SC.Location>
              )}
            </SC.ThirdHighlight>
          )}
          {imageProps && variant === 'article' && (
            <SC.ImageWrapper>
              <SC.CImage $variant={variant} {...imageProps} />
            </SC.ImageWrapper>
          )}
          {buttons && (
            <SC.ButtonsContainer ref={buttonContainerRef}>
              {buttons}
            </SC.ButtonsContainer>
          )}
        </SC.Foreground>
      </>
    )
  }, [
    backgroundColor,
    buttons,
    date,
    duration,
    firstHighlightText,
    imageProps,
    imgSrc,
    location,
    locationImage,
    secondHighlightText,
    tag,
    variant,
  ])

  if (singleButtonActionProps) {
    return (
      <SC.Card
        className={`content-card ${className}`}
        forceLandscape={forceLandscape}
        {...singleButtonActionProps}
      >
        {CardContent}
      </SC.Card>
    )
  }

  return (
    <SC.Card
      className={`content-card ${className}`}
      forceLandscape={forceLandscape}
    >
      {CardContent}
    </SC.Card>
  )
}

export default ContentCard
